import {isNotNil} from 'ramda';

import {RecordAuditResponseBody} from '@dms/api/core';

import {RecordAuditScorableResult, computeRecordAuditScore} from './computeRecordAuditScore';
import {RecordAuditCounts} from './RecordAuditCounts';

/**
 * Single source for a record audit's tallies + score: live recompute from `liveResults` while the
 * audit is OPEN (overlays applied), otherwise the BE-frozen `audit.statistics`. Used by both the
 * score bar and the detail header so the two never drift.
 */
export const resolveRecordAuditCounts = (
  audit: RecordAuditResponseBody,
  liveResults: ReadonlyArray<RecordAuditScorableResult> | undefined
): RecordAuditCounts => {
  if (audit.state === 'OPEN' && isNotNil(liveResults)) {
    const ok = liveResults.filter((result) => result.value === 'OK').length;
    const bad = liveResults.filter((result) => result.value === 'BAD').length;
    const na = liveResults.filter((result) => result.value === 'NA').length;
    const score = computeRecordAuditScore(liveResults, audit.definitionSnapshot.successThreshold);
    return {
      ok,
      bad,
      na,
      notFilled: liveResults.length - ok - bad - na,
      total: liveResults.length,
      okPoints: score.okPoints,
      maxPoints: score.maxPoints,
      percentage: score.percentage,
      passed: score.passed,
    };
  }

  const {okCount, badCount, naCount, totalCount, score} = audit.statistics;
  return {
    ok: okCount,
    bad: badCount,
    na: naCount,
    notFilled: totalCount - okCount - badCount - naCount,
    total: totalCount,
    okPoints: score.okPoints,
    maxPoints: score.maxPoints,
    percentage: score.percentage,
    passed: score.passed,
  };
};
